// Sentry must be initialized before all other imports
import * as Sentry from '@sentry/react';
import { initPostHog } from './lib/posthog';
import {
  CLIENT_SENTRY_REPLAY_PRIVACY,
  CLIENT_SENTRY_SEND_DEFAULT_PII,
  CEF_SHARP_EMBEDDED_CLIENT_UPDATE_REJECTION,
} from './lib/sentryConfig';

if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: 'production',
    sendDefaultPii: CLIENT_SENTRY_SEND_DEFAULT_PII,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(CLIENT_SENTRY_REPLAY_PRIVACY),
    ],
    // Performance monitoring
    tracesSampleRate: 0.1,
    // Session replay - 10% of sessions, 100% on error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Drop noise from browser extensions and link scanners injecting into the page
    // (Safari/Chrome/Firefox extensions, password managers, Outlook, etc.)
    ignoreErrors: [
      /Invalid call to runtime\.sendMessage/i,
      /Extension context invalidated/i,
      CEF_SHARP_EMBEDDED_CLIENT_UPDATE_REJECTION,
    ],
    denyUrls: [
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
      /safari-extension:\/\//i,
      /safari-web-extension:\/\//i,
    ],
  });
}

// Initialize PostHog analytics
initPostHog();

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';
import { AuthProvider } from './contexts/AuthContext';
import './index.css';
import App from './App.tsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

// Error fallback component for React crashes
// eslint-disable-next-line react-refresh/only-export-components
function ErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center p-8">
        <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
        <p className="text-muted-foreground mb-6">We've been notified and are looking into it.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
